import { ElementType, HTMLAttributes, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./Text.module.scss";

export type TextVariant =
    | "body-lg"
    | "body-md"
    | "body-sm"
    | "label-lg"
    | "label-md"
    | "label-sm"
    | "caption"
    | "overline";

export type TextTone =
    | "primary"
    | "secondary"
    | "tertiary"
    | "disabled"
    | "error"
    | "success"
    | "warning"
    | "link";

const variantClass: Record<TextVariant, string> = {
    "body-lg": styles.bodyLg,
    "body-md": styles.bodyMd,
    "body-sm": styles.bodySm,
    "label-lg": styles.labelLg,
    "label-md": styles.labelMd,
    "label-sm": styles.labelSm,
    caption: styles.caption,
    overline: styles.overline,
};

const toneClass: Record<TextTone, string> = {
    primary: styles.tonePrimary,
    secondary: styles.toneSecondary,
    tertiary: styles.toneTertiary,
    disabled: styles.toneDisabled,
    error: styles.toneError,
    success: styles.toneSuccess,
    warning: styles.toneWarning,
    link: styles.toneLink,
};

export type TextProps = {
    variant?: TextVariant;
    /**
     * Semantic text color. Omit to inherit: body/label/overline use surrounding color;
     * `caption` keeps the mixin default (secondary).
     */
    tone?: TextTone;
    /** Element to render; defaults to `p` for body variants, `span` otherwise. */
    as?: ElementType;
    /** Single-line ellipsis. */
    truncate?: boolean;
    className?: string;
    children?: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "color">;

function defaultElementForVariant(variant: TextVariant): ElementType {
    if (
        variant === "body-lg" ||
        variant === "body-md" ||
        variant === "body-sm"
    ) {
        return "p";
    }
    return "span";
}

/**
 * Treez DS — body copy and UI text using the Roboto ramp (`@/theme/typography` → `--ds-*`).
 * Use with {@link Heading} on new screens instead of legacy typography utilities.
 */
export function Text({
    variant = "body-md",
    tone,
    as,
    truncate: truncateProp,
    className,
    children,
    ...rest
}: TextProps) {
    const Component = (as ?? defaultElementForVariant(variant)) as ElementType;

    return (
        <Component
            className={cx(
                styles.root,
                variantClass[variant],
                tone != null && toneClass[tone],
                truncateProp && styles.truncate,
                className,
            )}
            {...rest}
        >
            {children}
        </Component>
    );
}
